import { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

// Auditoria do catálogo (secao 8).
//
// O catálogo é a régua contra a qual a conformidade de todos os clientes é
// medida: mudar a vigência de um treinamento muda a resposta a "este
// funcionário está em dia" para a carteira inteira. Esta tela responde a
// pergunta que aparece depois — "por que em março o sistema dizia que estava
// vencido".
//
// Só leitura, e só para o superusuário. A tabela não tem policy de escrita para
// ninguém: quem grava é o gatilho `zz_auditoria`. Auditoria que o auditado pode
// editar não é auditoria.

interface LinhaDeAuditoria {
  id: number;
  tabela: string;
  registro_id: string;
  registro_nome: string | null;
  acao: 'insert' | 'update' | 'delete';
  criado_em: string;
  autor: string | null;
  autor_nome: string;
  campos_alterados: string[] | null;
  mudancas: Record<string, { antes: unknown; depois: unknown }> | null;
}

const ROTULO_TABELA: Record<string, string> = {
  profissoes: 'Profissão',
  profissao_documentos: 'Documento da profissão',
  profissao_campos: 'Campo da profissão',
  treinamentos: 'Treinamento',
  treinamento_profissoes: 'Destinatário do treinamento',
  alvaras_templates: 'Tipo de alvará',
  alvara_template_itens: 'Item vinculado ao alvará',
  infraestrutura_categorias: 'Categoria de infraestrutura',
  infraestrutura_itens: 'Item de infraestrutura',
  infraestrutura_item_treinamentos: 'Treinamento do item',
};

const ACAO = {
  insert: { rotulo: 'Criado', classe: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
  update: { rotulo: 'Alterado', classe: 'bg-amber-100 text-amber-900 border-amber-200' },
  delete: { rotulo: 'Removido', classe: 'bg-red-100 text-red-900 border-red-200' },
} as const;

const POR_PAGINA = 50;

const fmtDataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Valor de uma coluna como texto curto. `null` vira "—", não "null". */
const comoTexto = (valor: unknown): string => {
  if (valor === null || valor === undefined) return '—';
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não';
  if (typeof valor === 'object') return JSON.stringify(valor);
  return String(valor);
};

export default function AdminAuditoriaPage() {
  const { isSuperuser } = useAuth();
  const [linhas, setLinhas] = useState<LinhaDeAuditoria[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtroTabela, setFiltroTabela] = useState('todas');
  const [pagina, setPagina] = useState(0);
  const [expandida, setExpandida] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      let consulta = supabase
        .from('vw_catalogo_auditoria')
        .select('*')
        .order('criado_em', { ascending: false })
        .range(pagina * POR_PAGINA, pagina * POR_PAGINA + POR_PAGINA - 1);

      if (filtroTabela !== 'todas') consulta = consulta.eq('tabela', filtroTabela);

      const { data, error } = await consulta;
      if (error) throw new Error(error.message);
      setLinhas((data ?? []) as unknown as LinhaDeAuditoria[]);
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar a auditoria.');
    } finally {
      setCarregando(false);
    }
  }, [filtroTabela, pagina]);

  useEffect(() => {
    if (isSuperuser) void carregar();
  }, [isSuperuser, carregar]);

  // Voltar para a primeira página ao trocar o filtro: manter a página 3 de uma
  // lista que agora tem uma página só mostra um resultado vazio que parece erro.
  useEffect(() => {
    setPagina(0);
  }, [filtroTabela]);

  const tabelas = useMemo(() => Object.keys(ROTULO_TABELA), []);

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta área é reservada para o superusuário administrador da plataforma.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <header className="mb-6">
          <h1 className="text-xl font-bold text-foreground-900">Auditoria do Catálogo</h1>
          <p className="text-sm text-foreground-500 mt-1">
            Quem alterou o quê, e quando, nos cadastros que valem para todos os clientes. Só
            leitura: as linhas são gravadas pelo banco, e ninguém as edita.
          </p>
        </header>

        {erro && (
          <div role="alert" className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3">
            <p className="text-sm text-red-700">{erro}</p>
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="filtro-tabela" className="sr-only">
            Filtrar por cadastro
          </label>
          <select
            id="filtro-tabela"
            value={filtroTabela}
            onChange={(e) => setFiltroTabela(e.target.value)}
            className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm max-w-xs"
          >
            <option value="todas">Todos os cadastros</option>
            {tabelas.map((t) => (
              <option key={t} value={t}>
                {ROTULO_TABELA[t]}
              </option>
            ))}
          </select>
        </div>

        {carregando ? (
          <p className="text-sm text-foreground-500">Carregando o histórico...</p>
        ) : linhas.length === 0 ? (
          <p className="text-sm text-foreground-500">
            Nenhuma alteração registrada{filtroTabela !== 'todas' ? ' para este cadastro' : ''}.
          </p>
        ) : (
          <ul className="space-y-2">
            {linhas.map((l) => (
              <li
                key={l.id}
                className="rounded-lg border border-background-200/70 bg-background-50 px-4 py-3"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${ACAO[l.acao].classe}`}
                      >
                        {ACAO[l.acao].rotulo}
                      </span>
                      <span className="text-sm font-semibold text-foreground-900">
                        {l.registro_nome || l.registro_id}
                      </span>
                      <span className="text-xs text-foreground-500">
                        {ROTULO_TABELA[l.tabela] ?? l.tabela}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {fmtDataHora(l.criado_em)} · {l.autor_nome}
                      {l.campos_alterados && l.campos_alterados.length > 0 && (
                        <> · {l.campos_alterados.length} campo(s)</>
                      )}
                    </p>
                  </div>

                  {l.acao === 'update' && l.mudancas && (
                    <button
                      type="button"
                      onClick={() => setExpandida(expandida === l.id ? null : l.id)}
                      aria-expanded={expandida === l.id}
                      className="text-xs font-medium text-primary-700 hover:underline shrink-0"
                    >
                      {expandida === l.id ? 'Ocultar' : 'Ver o que mudou'}
                    </button>
                  )}
                </div>

                {expandida === l.id && l.mudancas && (
                  <div className="mt-3 pt-3 border-t border-background-200/60 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-foreground-500">
                          <th className="pb-1 pr-4 font-medium">Campo</th>
                          <th className="pb-1 pr-4 font-medium">Antes</th>
                          <th className="pb-1 font-medium">Depois</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(l.mudancas).map(([campo, m]) => (
                          <tr key={campo} className="border-t border-background-200/40">
                            <td className="py-1 pr-4 font-medium text-foreground-700">{campo}</td>
                            <td className="py-1 pr-4 text-foreground-500 line-through">
                              {comoTexto(m.antes)}
                            </td>
                            <td className="py-1 text-foreground-900">{comoTexto(m.depois)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {(pagina > 0 || linhas.length === POR_PAGINA) && (
          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
              disabled={pagina === 0}
              className="px-3 py-1.5 rounded-md border border-background-300/60 text-sm text-foreground-700 disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="text-xs text-foreground-500">Página {pagina + 1}</span>
            <button
              type="button"
              onClick={() => setPagina((p) => p + 1)}
              disabled={linhas.length < POR_PAGINA}
              className="px-3 py-1.5 rounded-md border border-background-300/60 text-sm text-foreground-700 disabled:opacity-40"
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
